import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithAuth } from 'src/app-redux/rtkq.interceptor';
import { transformResponses } from 'src/app-redux/rtkq.interceptor';
import { apiResources } from 'src/common/Constants';
import { FetchRolesArgs, NewRoleArgs, Role, UpdateRoleArgs } from 'src/rbac/RolesAndPermissions';

const rolesTag = 'roles';

/** RTK Query API slice for role management endpoints. */
export const rolesApi = createApi({
  reducerPath: 'rolesApi',
  tagTypes: [rolesTag],
  baseQuery: baseQueryWithAuth,
  endpoints: (builder) => ({
    /** Query to fetch roles with optional pagination. */
    fetchRoles: builder.query<Role[], FetchRolesArgs>({
      providesTags: [rolesTag],
      query: (args) => {
        return {
          url: `${apiResources.roleV2}/all`,
          method: 'GET',
          params: args.all ? undefined : { page: args.page, pageSize: args.pageSize },
        };
      },
      ...transformResponses,
    }),
    /** Mutation to create a new role. */
    createRole: builder.mutation<void, NewRoleArgs>({
      query: (args) => {
        return {
          url: `${apiResources.roleV2}`,
          method: 'POST',
          body: args,
        };
      },
      ...transformResponses,
      invalidatesTags: (_result, error) => (error ? [] : [rolesTag]),
    }),
    /** Mutation to update an existing role. */
    updateRole: builder.mutation<void, UpdateRoleArgs>({
      query: (args) => {
        return {
          url: `${apiResources.roleV2}/${args.roleId}`,
          method: 'PUT',
          body: {
            name: args.name,
            permissions: args.permissions,
          },
        };
      },
      ...transformResponses,
      invalidatesTags: (_result, error) => (error ? [] : [rolesTag]),
    }),
    /** Mutation to delete a role by ID. */
    deleteRole: builder.mutation<void, number>({
      query: (roleId) => {
        return {
          url: `${apiResources.roleV2}/${roleId}`,
          method: 'DELETE',
        };
      },
      ...transformResponses,
      invalidatesTags: (_result, error) => (error ? [] : [rolesTag]),
    }),
  }),
});

/** RTK Query hooks for role management endpoints. */
export const { useFetchRolesQuery, useCreateRoleMutation, useUpdateRoleMutation, useDeleteRoleMutation } = rolesApi;
